import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { UsersIcon, Button, Skeleton, ZapIcon } from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { settingsTeamRoute } from '~publish/legacy/routes'
import {
  isEssentialsPlan,
  isFreePlan,
  isTeamPlan,
} from '~publish/pages/Settings/Billing/utils/planUtils'

import styles from './planCTA.module.css'

import type { JSX } from 'react'

const GetTeamMemberCount = graphql(/* GraphQL */ `
  query GetTeamMemberCount($input: TeamMembersInput!) {
    teamMembers(input: $input) {
      edges {
        node {
          id
        }
      }
    }
  }
`)

export function PlanCTA(): JSX.Element | null {
  const { account } = useAccount()
  const organizationId = useOrganizationId()
  const navigate = useNavigate()
  const openGlobalModal = useOpenGlobalModal()

  // The backend authorizes `teamMembers` on org role (admin only), so only
  // actors who can manage team members may fetch the count. Team plans
  // legitimately contain non-admin members who would otherwise fire this query
  // and get denied.
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')

  // Fetch team member count for team plan admins
  const { data: teamMembersData, loading: teamMembersLoading } = useQuery(
    GetTeamMemberCount,
    {
      variables: {
        input: {
          organizationId: organizationId ?? '',
          first: 100, // Fetch enough to get accurate count
        },
      },
      skip:
        !organizationId ||
        !account ||
        !isTeamPlan(account) ||
        !canManageTeamMembers,
    },
  )

  if (!account) {
    return null
  }

  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties || null
  const cta = 'publish-userMenu-planCTA'

  // Team plan: Navigate to team settings
  if (isTeamPlan(account)) {
    // The team settings page redirects non-admins away, so the CTA is
    // admin-only.
    if (!canManageTeamMembers) {
      return null
    }

    const handleInviteTeamClick = (): void => {
      BufferTracker.cTAClicked({
        organizationId: organizationId ?? '',
        product: Product.Publish,
        cta,
        clientName: 'publishWeb',
        upgradePathName: 'inviteTeam-teamPlan',
        ...commonTrackingProperties,
      })
      navigate(settingsTeamRoute.route)
    }

    // Show skeleton while loading team members to prevent flash
    if (teamMembersLoading) {
      return (
        <Skeleton
          height={32}
          className={styles.planCTAButton}
          aria-label="Loading team information"
        />
      )
    }

    // Determine if there are other team members besides the owner
    const memberCount = teamMembersData?.teamMembers?.edges?.length ?? 0
    const hasOtherTeamMembers = memberCount > 1
    const buttonText = hasOtherTeamMembers ? 'Manage Team' : 'Invite Team'

    return (
      <Button
        variant="secondary"
        className={styles.planCTAButton}
        onClick={handleInviteTeamClick}
      >
        <UsersIcon size="small" />
        {buttonText}
      </Button>
    )
  }

  // Free or Essentials plan: Open plan selector
  const handleUpgradeClick = (): void => {
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName: `accountMenu-upgrade`,
    })

    BufferTracker.cTAClicked({
      organizationId: organizationId ?? '',
      product: Product.Publish,
      cta,
      clientName: 'publishWeb',
      upgradePathName: `accountMenu-upgrade`,
      ...commonTrackingProperties,
    })
  }

  // Free plan: Show "Upgrade Plan"
  if (isFreePlan(account)) {
    return (
      <Button
        variant="secondary"
        className={styles.planCTAButton}
        onClick={handleUpgradeClick}
      >
        <ZapIcon size="small" />
        Upgrade Plan
      </Button>
    )
  }

  // Essentials plan: Show "Invite Team" with upgrade icon. Inviting a team is
  // an admin action, so the upsell is admin-only.
  if (isEssentialsPlan(account)) {
    if (!canManageTeamMembers) {
      return null
    }

    return (
      <Button
        variant="secondary"
        className={styles.planCTAButton}
        onClick={handleUpgradeClick}
      >
        <ZapIcon size="small" />
        Invite Team
      </Button>
    )
  }

  // For any other plans, don't show a CTA
  return null
}
